import {
  getAmount,
  getPercentage,
} from 'app/state/ducks/lenders-positions/thunks'
import { CleanedFundManagerData, FundManagerData, LendersPosition } from 'app/models/lenders-position-params'
import { formatNumberToCurrency } from 'app/utils/util-functions'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { getEntities } from 'app/state/ducks/entities/selectors'
import { Stack } from '@mantine/core'
import MainTable from 'app/views/components/Table/MainTable'
import { getColumnDefs } from './table-columnDefs-fund-manager'

interface Params {
  effectiveDate: Date
  currency: string
  lendersPositions: LendersPosition[]
  loading: boolean
}

export default function FundManagerLenderTable({ effectiveDate, currency, lendersPositions, loading }: Params) {

  const entities = useSelector(getEntities)
  const [lenderListData, setLenderListData] = useState(new Map())
  const totalAmountRef = useRef('');

  useEffect(() => {
    const fundManagerMap = new Map()
    lendersPositions.forEach((lp: LendersPosition) => {
      const entity = entities?.find(item => item.id == lp.lender.id)
      const fmParent = entity?.fundManager ?? entity?.parent ?? 'Missing FM/Parent'
      const amount = getAmount(lp, effectiveDate)
      const percentage = getPercentage(lp, effectiveDate)
      const mei = entity?.fundManagerMei ?? entity?.parentMei ?? entity?.mei ?? ''

      if (!fundManagerMap.get(fmParent)) {
        fundManagerMap.set(fmParent, { amount, percentage, mei })
      }
      else {
        fundManagerMap.set(fmParent, {
          amount: fundManagerMap.get(fmParent).amount + amount,
          percentage: fundManagerMap.get(fmParent).percentage + percentage,
          mei: fundManagerMap.get(fmParent).mei
        })
      }

    })
    setLenderListData(fundManagerMap)

  }, [lendersPositions, effectiveDate, entities])


  function cleanFundManagers(
    lenderListData: Map<string, FundManagerData>,
  ): CleanedFundManagerData[] {
    return (
      [...lenderListData.keys()].map((fundManager) => {
        const lenderData = lenderListData.get(fundManager)
        return {
          'Fund Manager / Parent': fundManager,
          'Allocation': formatNumberToCurrency(Number(lenderData?.amount ?? 0), currency),
          'MEI': lenderData?.mei ?? '',
          'Percentage': (lenderData?.percentage ?? '0') + '%',
        }
      }) ?? []
    );
  }

  // Computed during render (not in an effect) so the footer — which reads the
  // ref through a stable callback — is never one render behind the as-of date.
  // A ref (not useMemo + fresh closure) is required: MainTable freezes its
  // columnDefs on first render (useMemo with [] deps), so the footer holds the
  // FIRST getTotalAmount closure forever — a stable callback reading this ref
  // is the only channel that delivers the current total into that frozen closure.
  totalAmountRef.current = formatNumberToCurrency(
    lendersPositions
      ? lendersPositions.reduce(
        (acc: number, curr: LendersPosition) => acc + getAmount(curr, effectiveDate),
        0
      )
      : 0,
    currency ?? 'USD'
  )

  const getTotalAmount = useCallback(() => totalAmountRef.current, []);

  return (
    <Stack w="100%">
      <MainTable
        tableName='Lender History'
        csvExportEnabled={true}
        columnDefs={getColumnDefs(getTotalAmount)}
        data={cleanFundManagers(lenderListData)}
        enableTopToolbar={false}
        headerBackgroundColor='#F0EEEE'
        setRow={() => {
        }}
        isLoading={loading}
      />
    </Stack>

  )
}